import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/slw/shared/lib/utils';

const inputVariants = cva(
  `flex w-full border bg-transparent rounded-md px-5 py-2 text-base
          transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground
          placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring
          disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-all duration-200 ease-in-out`,
  {
    variants: {
      size: {
        sm: 'h-10',
        md: 'h-12',
        lg: 'h-13.25',
        default: 'h-12',
      },
      color: {
        default: 'bg-secondary text-gray-900 focus-visible:ring-default',
        white: 'bg-secondary text-gray-900 focus-visible:ring-default',
        main: 'bg-primary text-secondary-foreground focus-visible:ring-default',
        darkBlue: 'bg-dark-blue-input text-white focus-visible:ring-default',
        darkGrey: 'bg-dark-grey-input text-white focus-visible:ring-default',
        grey: 'bg-gray-input text-gray-900 focus-visible:ring-default',
        light: 'bg-light-input text-gray-900 focus-visible:ring-default',
      },
    },
    defaultVariants: {
      size: 'default',
      color: 'default',
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'color' | 'size'>,
    VariantProps<typeof inputVariants> {
  asChild?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, color = 'default', size = 'default', ...props }, ref) => {
    return <input type={type} className={cn(inputVariants({ size, color }), className)} ref={ref} {...props} />;
  },
);

Input.displayName = 'Input';

export { Input };
